'use client';

import { XIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import HeaderMobileActions from './HeaderMobileActions';
import HeaderMobileNavItem from './HeaderMobileNavItem';
import HeaderMobileUserSection from './HeaderMobileUserSection';
import { ICON_SIZE } from '../../lib/header-constants';
import useHeaderMobileAccordion from '../../hooks/header/useHeaderMobileAccordion';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext } from './context';
import type { NavItem } from '../../types/navigation';

interface HeaderMobileDrawerProps {
  isOpen: boolean;
  navItems: NavItem[];
  onClose: () => void;
}

const HeaderMobileDrawer = ({ isOpen, navItems, onClose }: HeaderMobileDrawerProps) => {
  const { hideUserActions, user } = useNavbarContext();
  const [isMounted, setIsMounted] = useState(false);

  const { expandedItems, expandedMegaGroups, toggleAccordion, toggleMegaGroup } =
    useHeaderMobileAccordion({ isOpen, navItems });

  const renderMobileNavItem = (item: NavItem) => {
    const handleToggleItem = () => {
      toggleAccordion(item.label);
    };

    const handleToggleGroup = (groupId: string) => {
      toggleMegaGroup(item.label, groupId);
    };

    return (
      <HeaderMobileNavItem
        expandedGroupIds={expandedMegaGroups[item.label] || []}
        isExpanded={expandedItems.includes(item.label)}
        item={item}
        key={item.label}
        onClose={onClose}
        onToggleGroup={handleToggleGroup}
        onToggleItem={handleToggleItem}
      />
    );
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className={cn(
          'global-header-mobile-backdrop bg-adipa-text-primary/15 duration-adipa-base fixed inset-0 transition-opacity ease-in-out',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />

      <aside
        aria-label="Menú principal móvil"
        className={cn(
          'global-header-mobile-drawer border-adipa-border bg-adipa-surface shadow-adipa-console-panel fixed right-0 top-0 flex h-full w-full max-w-none flex-col border-l sm:max-w-sm',
          'duration-adipa-base transition-transform ease-in-out',
          isOpen ? 'global-header-drawer-open' : 'global-header-drawer-closed',
        )}
      >
        <div className="border-adipa-border flex items-center justify-between border-b py-3 pl-4 pr-3">
          <p className="text-adipa-text-primary text-sm font-semibold">Menú</p>
          <button
            aria-label="Cerrar menú"
            className={cn(
              'rounded-adipa-lg text-adipa-text-secondary inline-flex h-10 w-10 items-center justify-center',
              'duration-adipa-base hover:bg-adipa-surface-muted hover:text-adipa-primary transition-colors ease-in-out',
              'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
            )}
            type="button"
            onClick={onClose}
          >
            <XIcon size={ICON_SIZE} weight="bold" />
          </button>
        </div>

        <nav aria-label="Navegación principal móvil" className="flex-1 overflow-y-auto px-3 py-4">
          {!hideUserActions && user && (
            <HeaderMobileUserSection isDrawerOpen={isOpen} user={user} onClose={onClose} />
          )}
          <ul className="mt-3 space-y-1.5">{navItems.map(renderMobileNavItem)}</ul>
        </nav>

        {!hideUserActions && <HeaderMobileActions />}
      </aside>
    </>,
    document.body,
  );
};

export default HeaderMobileDrawer;
